import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InvoiceUploader from "@/components/InvoiceUploader";
import { roleLabel } from "@/lib/roles";
import { toast } from "sonner";

const STATUS = {
  pending: ["bg-amber-500", "Pending verification"],
  verified: ["bg-emerald-600", "Verified"],
  rejected: ["bg-rose-600", "Rejected"],
};

const StatusBlock = ({ data }) => {
  const v = data.verified;
  const req = data.request;
  return (
    <div className="space-y-2 text-sm">
      <div data-testid="my-bank-verified">
        <span className="text-slate-500">Verified details in use: </span>
        {v.account_masked ? <b>{v.bank_name} {v.account_masked} · {v.ifsc}</b> : <span className="text-rose-600">None yet</span>}
      </div>
      {req && (
        <div data-testid="my-bank-request-status">
          <Badge className={STATUS[req.status]?.[0]}>{STATUS[req.status]?.[1] || req.status}</Badge>
          <span className="ml-2 text-xs text-slate-500">Last submitted {(req.created_at || "").slice(0, 10)}: {req.bank_name} {req.account_masked} · {req.ifsc}</span>
          {req.status === "rejected" && <div className="text-xs text-rose-700 mt-1" data-testid="my-bank-reject-reason">Reason: {req.reject_reason}</div>}
        </div>
      )}
    </div>
  );
};

const BankForm = ({ onSaved }) => {
  const [f, setF] = useState({ bank_name: "", account_number: "", ifsc: "", pan: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error("Upload a cancelled cheque or passbook photo"); return; }
    setBusy(true);
    try {
      await api.post("/v2/me/bank-details", { ...f, proof_file_id: files[0].id });
      toast.success("Submitted. Accounts will verify your details.");
      setF({ bank_name: "", account_number: "", ifsc: "", pan: "" }); setFiles([]); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Submit failed"); }
    setBusy(false);
  };
  const field = (k, label, extra = {}) => (
    <div><Label>{label}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`my-bank-${k}`} {...extra} /></div>
  );
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="my-bank-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field("bank_name", "Bank name", { required: true })}
        {field("account_number", "Account number", { required: true, inputMode: "numeric" })}
        {field("ifsc", "IFSC", { required: true, maxLength: 11, placeholder: "e.g. SBIN0001234" })}
        {field("pan", "PAN (optional)", { maxLength: 10 })}
      </div>
      <InvoiceUploader label="Cancelled cheque or passbook photo" files={files} onChange={setFiles} required multiple={false} testId="my-bank-proof" />
      <Button type="submit" disabled={busy} data-testid="my-bank-submit">Submit for verification</Button>
    </form>
  );
};

export default function Profile() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = async () => { const r = await api.get("/v2/me/bank-details"); setData(r.data); };
  useEffect(() => { load(); }, []);
  return (
    <div className="p-6 lg:p-8 max-w-3xl" data-testid="profile-page">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Account</div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">My Profile</h1>
      <p className="text-sm text-slate-500 mt-2" data-testid="profile-identity">{user?.name} · {user?.email} · {roleLabel(user?.role)}{user?.employee_code ? ` · ${user.employee_code}` : ""}</p>
      <Card className="mt-6">
        <CardContent className="p-5 space-y-4">
          <div className="font-semibold">My bank details</div>
          <p className="text-xs text-slate-500">Used to pay your reimbursements. New or changed details are checked by Accounts first; until then your previously verified details stay in use.</p>
          {data ? <StatusBlock data={data} /> : <div className="text-sm text-slate-500">Loading…</div>}
          <BankForm onSaved={load} />
        </CardContent>
      </Card>
    </div>
  );
}
